import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StarRating from '@/components/base/StarRating';
import { siteInfo, reviews } from '@/mocks/data';

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    if (formData.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams(new FormData(form) as unknown as Record<string, string>).toString();
      const response = await fetch('https://readdy.ai/api/form/d9otc1qftmtnfcfh0dng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_e) { parsed = {}; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam') || msg.includes('垃圾')) {
          setFormStatus('success');
          setFormData({ name: '', email: '', message: '' });
          setTimeout(() => setFormStatus('idle'), 3000);
        } else {
          setFormError(msg);
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `${siteInfo.name}致力于为漫画爱好者提供高品质的在线免费阅读服务`,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteInfo.address,
      },
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-8">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">关于我们</h1>
          <p className="text-sm text-foreground-500 mb-10">了解古古漫画网的故事与使命</p>

          <section className="mb-16">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-10">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">品牌故事</h2>
              <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-3">
                <p>
                  {siteInfo.name}成立于2024年，由一群热爱漫画的年轻人创立。我们的使命很简单——让每一个漫画爱好者都能轻松、
                  免费地阅读到自己喜欢的漫画作品。
                </p>
                <p>
                  我们深知，漫画不仅仅是一种娱乐方式，更是一种文化的传承和艺术的表达。从热血沸腾的战斗场面到细腻动人的情感描绘，
                  漫画承载了无数创作者的激情与梦想。{siteInfo.name}希望成为连接创作者与读者之间的桥梁，
                  让更多优秀的漫画作品被看到、被欣赏。
                </p>
                <p>
                  网站以<strong>《终末的女武神》</strong>为核心内容，这部由梅村真也原作、アジチカ作画、
                  フクイタクミ构成的史诗级格斗漫画，以其宏大的世界观和精彩的战斗设计，
                  在全球范围内收获了超高人气。古古漫画网致力于为中文读者提供最及时、最完整的阅读体验。
                </p>
                <p>
                  未来，我们将不断扩展漫画库，引入更多经典与新锐作品，持续优化阅读体验，
                  让{siteInfo.name}成为华语漫画爱好者首选的一站式阅读平台。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reviews.map((review) => (
                <div key={review.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 shrink-0">
                      <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <StarRating rating={review.rating} />
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <p className="text-xs text-foreground-400 mt-2">{review.date}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">联系方式</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-500 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                        {siteInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-500 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">客服电话</p>
                      <a href={`tel:${siteInfo.phone}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                        {siteInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-500 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">给我们留言</h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="about-contact-form"
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  {formError && (
                    <div className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-2.5">{formError}</div>
                  )}
                  {formStatus === 'success' && (
                    <div className="text-sm text-green-600 bg-green-50 rounded-md px-4 py-2.5">留言提交成功！感谢您的反馈。</div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交留言'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}